import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BdAppEnvironmentService } from '@bindoc-projects/platform-lib/data';
import { ShareableType } from '@bindoc-projects/resource-domain-lib';
import { ApiEndpointKey } from '@bindoc-projects/app-config-domain-lib';
import { BehaviorSubject, Observable, switchMap, timer } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';

import { BdGrantRequestsStore } from '../store/grant-requests/grant-requests.store';
import {
  AcceptGrantRequestPayload,
  CreateGrantRequestPayload,
  GrantRequest,
  GrantRequestStatus,
} from './grant-request.model';

const POLL_INTERVAL_MS = 30_000;

@Injectable({ providedIn: 'root' })
export class BdGrantRequestsService {
  private readonly _httpClient = inject(HttpClient);
  private readonly _environmentService = inject(BdAppEnvironmentService);
  private readonly _store = new BdGrantRequestsStore(
    this._environmentService.load(ApiEndpointKey.BD_PERMISSION_RESOURCE_URL),
  );

  private readonly _refresh$ = new BehaviorSubject<void>(undefined);

  public readonly inboxCount$: Observable<number> = this._refresh$.pipe(
    switchMap(() =>
      timer(0, POLL_INTERVAL_MS).pipe(switchMap(() => this.inbox())),
    ),
    map((requests) => requests.length),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public refreshInbox(): void {
    this._refresh$.next();
  }

  public create(payload: CreateGrantRequestPayload): Observable<GrantRequest> {
    return this._httpClient.post<GrantRequest>(this._store.base(), payload);
  }

  public myRequests(status?: GrantRequestStatus): Observable<GrantRequest[]> {
    return this._httpClient.get<GrantRequest[]>(this._store.myRequests(), {
      params: this._statusParams(status),
    });
  }

  public inbox(): Observable<GrantRequest[]> {
    return this._httpClient.get<GrantRequest[]>(this._store.inbox());
  }

  public forShareable(
    type: ShareableType,
    id: string,
    status?: GrantRequestStatus,
  ): Observable<GrantRequest[]> {
    return this._httpClient.get<GrantRequest[]>(
      this._store.forShareable(type, id),
      { params: this._statusParams(status) },
    );
  }

  public accept(
    requestId: string,
    payload: AcceptGrantRequestPayload,
  ): Observable<GrantRequest> {
    return this._httpClient.put<GrantRequest>(
      this._store.accept(requestId),
      payload,
    );
  }

  public dismiss(requestId: string): Observable<GrantRequest> {
    return this._httpClient.put<GrantRequest>(this._store.dismiss(requestId), {});
  }

  private _statusParams(status?: GrantRequestStatus): HttpParams {
    let params = new HttpParams();
    if (status) {
      params = params.set('status', status);
    }
    return params;
  }
}
